import React, { useContext, useEffect, useState } from 'react';
import { Nav } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SimpleBar from 'simplebar-react';
import { ChevronDown, ChevronLeft, Search, X } from 'react-feather';
import SidebarData, { SIDEBAR_OPTIONS } from '../sidebardata/SidebarData';
import Logo from '../../logo/Logo';
import NavItemContainer from './NavItemContainer';
import NavSubMenu from './NavSubMenu';
import useFavoritosSidebar from './useFavoritosSidebar';
import { AuthContext } from '../../../context/authContext';
import { getItem, setItem, STORAGE_KEYS, useLocalStorage } from '../../../services/storage';
import { ToggleMiniSidebar } from '../../../store/customizer/CustomizerSlice';

const DESCUENTOS_ALLOWED_EMAILS = [
  'asanjose@cedetecgroup.com',
  'nfeneux@cedetecgroup.com',
  'ajimenez@cedetecgroup.com',
];
const PRESUPUESTOS_ALLOWED_EMAILS = [
  'asanjose@cedetecgroup.com',
  'nfeneux@cedetecgroup.com',
  'ajimenez@cedetecgroup.com',
];

// Items que SEDE y SECRETARÍA no ven (ven todo lo demás, por exclusión).
const EXCLUIDOS_SEDE_SECRETARIA = [
  SIDEBAR_OPTIONS.LOGS,
  SIDEBAR_OPTIONS.CALCULO_NOTAS,
  SIDEBAR_OPTIONS.SUPLANTACION_IDENTIDAD,
  SIDEBAR_OPTIONS.AJUSTES,
  SIDEBAR_OPTIONS.FACTURAS,
  SIDEBAR_OPTIONS.RECIBOS,
  SIDEBAR_OPTIONS.FACTURAR_RECIBOS,
  SIDEBAR_OPTIONS.CLIENTES,
];

// Delegación y Orientación solo ven "Alumnos" y "Matrículas" (de sus alumnos).
const INCLUIDOS_DELEGACION_ORIENTACION = [SIDEBAR_OPTIONS.ALUMNOS, SIDEBAR_OPTIONS.MATRICULAS];

// Decide si el rol (userOU) ve un item del menú. Debe ir en sync con los
// PrivateRoute de Router.js.
export const puedeVerItem = (id, { userOU, puedeVerDescuentos, puedeVerPresupuestos }) => {
  const { idCedesca, idCntec } = userOU;
  const esAdmin = idCedesca === 0 && idCntec === 0;
  const esSecretaria = idCedesca === -3 && idCntec === -3;

  // Soporte (tickets): Administradores/IT y Secretaría, alineado con la ruta
  // /tickets (PrivateRoute SUPERADMIN+SECRETARIA).
  if (id === SIDEBAR_OPTIONS.TICKETS) return esAdmin || esSecretaria;

  // Descuentos y Presupuestos: solo Administradores/IT y emails autorizados.
  if (id === SIDEBAR_OPTIONS.DESCUENTOS) return esAdmin || puedeVerDescuentos;
  if (id === SIDEBAR_OPTIONS.PRESUPUESTOS) return esAdmin || puedeVerPresupuestos;

  // Administrador/IT: lo ve todo.
  if (esAdmin) return true;

  // Delegación: basta pertenecer a UNA empresa (id > 0). Las delegaciones de
  // una sola sede traen la otra en null; usar > 0 (no !==) evita que ese null
  // cuele aquí sin dar rol en getUserRol (rutas bloqueadas).
  if (idCedesca > 0 || idCntec > 0) return INCLUIDOS_DELEGACION_ORIENTACION.includes(id);

  // Sede y Secretaría: todo menos los excluidos.
  if ((idCedesca === -1 && idCntec === -1) || esSecretaria) {
    return !EXCLUIDOS_SEDE_SECRETARIA.includes(id);
  }

  // Orientación (histórico: solo se compara idCedesca).
  if (idCedesca === -2) return INCLUIDOS_DELEGACION_ORIENTACION.includes(id);

  // Si aún no se ha obtenido el rol, solo la página inicial "Alumnos".
  if (idCedesca === -4 && idCntec === -4) return id === SIDEBAR_OPTIONS.ALUMNOS;

  return false;
};

// Agrupa SidebarData en bloques { caption, items } (cada caption abre uno) y
// deja solo los items visibles; un bloque sin items visibles desaparece con
// su caption. Los items con submenú se muestran siempre, como hasta ahora.
export const bloquesVisibles = (data, permisos) => {
  const bloques = [];
  data.forEach((navi) => {
    if (navi.caption) {
      bloques.push({ caption: navi.caption, items: [] });
      return;
    }
    if (!navi.children && !puedeVerItem(navi.id, permisos)) return;
    if (bloques.length === 0) bloques.push({ caption: null, items: [] });
    bloques[bloques.length - 1].items.push(navi);
  });
  return bloques.filter((bloque) => bloque.items.length > 0);
};

const sinTildes = (t) =>
  String(t)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const esRutaDeItem = (pathname, href) => pathname === href || pathname.startsWith(`${href}/`);

const Sidebar = () => {
  const location = useLocation();
  const currentURL = location.pathname.split('/').slice(0, -1).join('/');

  const activeBg = useSelector((state) => state.customizer.sidebarBg);
  const isFixed = useSelector((state) => state.customizer.isSidebarFixed);
  // En modo mini los captions no se ven (no se podría desplegar): se ignora el plegado.
  const isMini = useSelector((state) => state.customizer.isMiniSidebar);
  const dispatch = useDispatch();
  useEffect(() => {
    setItem(STORAGE_KEYS.SIDEBAR_MINI, Boolean(isMini));
  }, [isMini]);
  // Captions de los bloques plegados por el usuario (persistido por navegador).
  const [plegadosGuardados, setPlegados] = useLocalStorage(STORAGE_KEYS.SIDEBAR_PLEGADOS, []);
  const plegados = Array.isArray(plegadosGuardados) ? plegadosGuardados : [];
  const { userOU } = useContext(AuthContext);
  const [busqueda, setBusqueda] = useState('');
  const { favoritos, alternar, arrastreDe } = useFavoritosSidebar();
  const userSession = getItem(STORAGE_KEYS.USER);
  const userEmail = userSession?.profileObj?.email;
  const bloques = bloquesVisibles(SidebarData, {
    userOU,
    puedeVerDescuentos: DESCUENTOS_ALLOWED_EMAILS.includes(userEmail),
    puedeVerPresupuestos: PRESUPUESTOS_ALLOWED_EMAILS.includes(userEmail),
  });

  // Al llegar a una pantalla de un bloque plegado, se despliega para que se vea
  // dónde se está.
  const captionActivo = bloques.find((bloque) =>
    bloque.items.some((navi) => navi.href && esRutaDeItem(location.pathname, navi.href)),
  )?.caption;
  useEffect(() => {
    if (captionActivo && plegados.includes(captionActivo)) {
      setPlegados(plegados.filter((caption) => caption !== captionActivo));
    }
    // Solo al cambiar de pantalla: plegar a mano el bloque activo debe respetarse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const alternarBloque = (caption) =>
    setPlegados(
      plegados.includes(caption) ? plegados.filter((c) => c !== caption) : [...plegados, caption],
    );

  const items = bloques.flatMap((bloque) => bloque.items);
  const q = sinTildes(busqueda.trim());
  const resultados = q ? items.filter((navi) => sinTildes(navi.title).includes(q)) : [];
  const itemsFavoritos = favoritos
    .map((id) => items.find((navi) => navi.id === id && !navi.children))
    .filter(Boolean);

  const renderItem = (navi, extra = {}) =>
    navi.children ? (
      <NavSubMenu
        key={navi.id}
        icon={navi.icon}
        title={navi.title}
        items={navi.children}
        suffix={navi.suffix}
        suffixColor={navi.suffixColor}
        isUrl={currentURL === navi.href}
      />
    ) : (
      <NavItemContainer
        key={navi.id}
        id={navi.id}
        className={location.pathname === navi.href ? 'activeLink' : ''}
        to={navi.href}
        title={navi.title}
        suffix={navi.suffix}
        suffixColor={navi.suffixColor}
        icon={navi.icon}
        esFavorito={favoritos.includes(navi.id)}
        onFavorito={() => alternar(navi.id)}
        {...extra}
      />
    );

  return (
    <div className={`sidebarBox shadow bg-${activeBg} ${isFixed ? 'fixedSidebar' : ''}`}>
      <button
        type="button"
        className={`sidebarToggle d-none d-lg-flex${isMini ? ' mini' : ''}`}
        onClick={() => dispatch(ToggleMiniSidebar())}
        aria-label={isMini ? 'Expandir menú' : 'Contraer menú'}
        title={isMini ? 'Expandir menú' : 'Contraer menú'}
      >
        <ChevronLeft size={14} aria-hidden="true" />
      </button>
      <SimpleBar style={{ height: '100%' }}>
        {/********Logo*******/}
        <div className="d-flex p-3 align-items-center">
          <Logo />
        </div>
        {/********Sidebar Content*******/}
        <div className="p-3 pt-1 mt-2 sidebarContenido">
          <div className="sidebarBuscar hide-mini">
            <Search size={14} className="sidebarBuscarIcono" aria-hidden="true" />
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar en el menú…"
              aria-label="Buscar en el menú"
            />
            {busqueda && (
              <button
                type="button"
                className="sidebarBuscarLimpiar"
                aria-label="Borrar búsqueda"
                onClick={() => setBusqueda('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <Nav vertical className={activeBg === 'white' ? '' : 'lightText'}>
            {q ? (
              <>
                {resultados.map((navi) => renderItem(navi))}
                {resultados.length === 0 && (
                  <span className="navCaption fw-bold">Sin resultados</span>
                )}
              </>
            ) : (
              <>
                {itemsFavoritos.length > 0 && (
                  <>
                    <span className="navCaption fw-bold mt-0">Favoritos</span>
                    {itemsFavoritos.map((navi) =>
                      renderItem(navi, { arrastre: arrastreDe(navi.id) }),
                    )}
                  </>
                )}
                {bloques.map((bloque, index) => {
                  const plegado =
                    !isMini && Boolean(bloque.caption) && plegados.includes(bloque.caption);
                  return (
                    <React.Fragment key={bloque.caption || 'sin-bloque'}>
                      {bloque.caption && (
                        <button
                          type="button"
                          className={`navCaption navCaptionToggle fw-bold ${
                            index === 0 && itemsFavoritos.length === 0 ? 'mt-0' : 'mt-4'
                          }`}
                          aria-expanded={!plegado}
                          onClick={() => alternarBloque(bloque.caption)}
                        >
                          <span>{bloque.caption}</span>
                          <ChevronDown size={14} aria-hidden="true" className="navCaptionChevron" />
                        </button>
                      )}
                      {!plegado && bloque.items.map((navi) => renderItem(navi))}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </Nav>
        </div>
      </SimpleBar>
    </div>
  );
};

export default Sidebar;
